import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Spinner, useEmbedded } from '../../../ui/v2';
import { Callout, EmptyState, Reason } from '../../../ui/v2';
import {
  Checkbox,
  Field,
  Input,
  Radio,
  Switch,
  Textarea,
  TimeInput,
} from '../../../ui/v2';
import { Chip, SaveBar } from '../../../ui/v2';
import { ConfirmDialog, Dialog, Popover, RowMenu } from '../../../ui/v2';
import { StatusLabel, Tag } from '../../../ui/v2';
import {
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableSkeletonRows,
} from '../../../ui/v2';
import { IconCalendar, IconClock, IconCopy, IconPlus } from '../../../ui/v2';
import {
  combineQueries,
  useHoursActions,
  useSpecialHoursQuery,
  useStoreHoursQuery,
  useStoreInfoQuery,
} from './data';
import {
  type SpecialHour,
  type WeeklyHoursDraft,
  formatDate,
  hoursSourceLabel,
  normalizeTime,
  to12h,
  toStoreHourPayload,
  toWeeklyDraft,
} from './model';
import {
  Block,
  QueryGate,
  SavedNote,
  SectionBody,
  SectionHead,
  useSavedAt,
} from './shared';

/**
 * Hours.
 *
 * - Weekly hours: the source (automatic from POS/Google vs manual) is a pair
 *   of radios that save immediately. In automatic mode the table is
 *   read-only text — not greyed-out inputs. In manual mode each row has a
 *   Closed checkbox and two time inputs; changes collect in the SaveBar.
 * - Special hours: a table of upcoming dates; add/edit in one Dialog, delete
 *   behind a ConfirmDialog (it can't be undone).
 */
const HoursSection: React.FC = () => {
  const weekly = useWeeklyHours();
  return (
    <SectionBody measure="hours" footer={weekly.footer}>
      <SectionHead title="Hours" />
      <div className="mt-6">
        {weekly.block}
        <SpecialBlock />
      </div>
    </SectionBody>
  );
};

// ---------------------------------------------------------------------------
// Weekly hours
// ---------------------------------------------------------------------------

/**
 * Weekly hours on their own, with their SaveBar: the Getting started step.
 * `manualOnly` leaves out the choice of source: during setup the store just
 * sets its hours, and saving them makes the schedule manual.
 */
export const WeeklyHoursEditor: React.FC<{
  manualOnly?: boolean;
  /** Told whenever the table gets, or loses, unsaved changes. */
  onDirtyChange?: (dirty: boolean) => void;
}> = ({ manualOnly = false, onDirtyChange }) => {
  const weekly = useWeeklyHours(manualOnly);
  const { dirty } = weekly;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  return (
    <>
      {weekly.block}
      {weekly.footer}
    </>
  );
};

/** Block + SaveBar returned separately so the bar spans the full column. */
function useWeeklyHours(manualOnly = false): {
  block: React.ReactNode;
  footer: React.ReactNode;
  dirty: boolean;
} {
  const hours = useStoreHoursQuery();
  const storeInfo = useStoreInfoQuery();
  const todayDay = storeInfo.data?.today_day_of_week;
  const hoursActions = useHoursActions();
  const [draft, setDraft] = useState<WeeklyHoursDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [invalidDay, setInvalidDay] = useState<string | null>(null);
  // The last "Copy to…" apply, for the inline Undo. Any other edit, save or
  // discard supersedes it, so Undo never reverts more than the copy.
  const [copied, setCopied] = useState<{
    source: string;
    targets: string[];
    prev: WeeklyHoursDraft[];
  } | null>(null);
  const [savedAt, markSaved] = useSavedAt();
  const [savedText, setSavedText] = useState('Saved.');
  // On its own page the table bleeds to the section gutter; embedded, it
  // stays inside the step.
  const embedded = useEmbedded();

  const data = hours.data;
  const manual =
    manualOnly ||
    (data?.use_manual_hours ?? data?.hours_source?.toLowerCase() === 'manual');
  const base = data ? toWeeklyDraft(data.hours) : [];
  useEffect(() => {
    if (data) {
      setDraft(toWeeklyDraft(data.hours));
      setError(null);
      setInvalidDay(null);
      setCopied(null);
    }
  }, [data]);

  const changed = draft.filter(
    (row, i) => JSON.stringify(row) !== JSON.stringify(base[i]),
  ).length;
  const dirty = manual && changed > 0;

  const update = (day: string, patch: Partial<WeeklyHoursDraft>) => {
    setDraft((cur) =>
      cur.map((r) => (r.day_of_week === day ? { ...r, ...patch } : r)),
    );
    setError(null);
    setCopied(null);
    if (invalidDay === day) setInvalidDay(null);
  };

  /** "Copy to…": the source day's hours onto the target days, in the draft. */
  const copyHours = (sourceDay: string, targets: string[]) => {
    const source = draft.find((r) => r.day_of_week === sourceDay);
    if (!source || targets.length === 0) return;
    setCopied({ source: sourceDay, targets, prev: draft });
    setDraft((cur) =>
      cur.map((r) =>
        targets.includes(r.day_of_week)
          ? {
              ...r,
              is_closed: source.is_closed,
              opening_time: source.opening_time,
              closing_time: source.closing_time,
            }
          : r,
      ),
    );
    setError(null);
    setInvalidDay(null);
  };

  const undoCopy = () => {
    if (!copied) return;
    setDraft(copied.prev);
    setCopied(null);
  };

  const setSource = async (next: boolean) => {
    if (next === manual || switching) return;
    setSwitching(true);
    setSwitchError(null);
    try {
      await hoursActions.setSource(next);
      setSavedText(
        next
          ? "Manual schedule on. Edits here won't follow POS changes."
          : 'Automatic schedule on. Hours are read-only here.',
      );
      markSaved();
    } catch (e) {
      setSwitchError(
        e instanceof Error
          ? e.message
          : 'Could not change the schedule source.',
      );
    } finally {
      setSwitching(false);
    }
  };

  const save = async () => {
    const bad = draft.find(
      (r) => !r.is_closed && (!r.opening_time || !r.closing_time),
    );
    if (bad) {
      setInvalidDay(bad.day_of_week);
      setError(
        `Set both opening and closing times for ${bad.day_of_week}, or mark it closed.`,
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await hoursActions.saveWeekly(draft.map(toStoreHourPayload));
      setSavedText('Saved.');
      setCopied(null);
      markSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save store hours.');
    } finally {
      setSaving(false);
    }
  };

  const savedNote = <SavedNote when={savedAt}>{savedText}</SavedNote>;
  const body = (
    <>
      <QueryGate
        query={hours}
        skeleton={
          <Table fixed>
            <tbody>
              <TableSkeletonRows rows={7} cols={4} />
            </tbody>
          </Table>
        }
      >
        {(h) => (
          <>
            {!manualOnly && (
              <SourceChoice
                source={h.hours_source}
                manual={!!manual}
                switching={switching}
                switchError={switchError}
                onChange={(next) => void setSource(next)}
              />
            )}
            {manual ? (
              <>
                <WeeklyEditList
                  rows={draft}
                  todayDay={todayDay}
                  invalidDay={invalidDay}
                  saving={saving}
                  embedded={embedded}
                  onChange={update}
                  onCopy={copyHours}
                  copiedDays={copied?.targets}
                />
                {copied && (
                  <p
                    role="status"
                    className={clsx(
                      'mt-2 flex flex-wrap items-center gap-x-2 text-v2-12 text-v2-fg-3',
                      !embedded && 'px-4 lg:px-6',
                    )}
                  >
                    <span>
                      {copied.source}’s hours applied to{' '}
                      {dayList(copied.targets)}.
                    </span>
                    <Button size="sm" variant="ghost" onClick={undoCopy}>
                      Undo
                    </Button>
                  </p>
                )}
              </>
            ) : (
              <TableFrame embedded={embedded}>
                <Table
                  fixed
                  className={clsx(
                    embedded
                      ? 'min-w-[520px]'
                      : 'mt-4 -mx-4 w-[calc(100%+32px)] lg:-mx-6 lg:w-[calc(100%+48px)]',
                  )}
                >
                  <THead>
                    <tr>
                      <TH width={148}>Day</TH>
                      <TH width={120}>Status</TH>
                      <TH>Opens</TH>
                      <TH>Closes</TH>
                    </tr>
                  </THead>
                  <TBody>
                    {draft.map((row) => (
                      <TR key={row.day_of_week}>
                        <TD className="font-medium">
                          <span className="inline-flex items-center gap-2">
                            {row.day_of_week}
                            {row.day_of_week === todayDay && <Tag>Today</Tag>}
                          </span>
                        </TD>
                        <TD>
                          <StatusLabel
                            size="sm"
                            tone={row.is_closed ? 'neutral' : 'success'}
                          >
                            {row.is_closed ? 'Closed' : 'Open'}
                          </StatusLabel>
                        </TD>
                        <TD
                          num
                          className={clsx(row.is_closed && 'text-v2-fg-4')}
                        >
                          {row.is_closed ? '—' : to12h(row.opening_time)}
                        </TD>
                        <TD
                          num
                          className={clsx(row.is_closed && 'text-v2-fg-4')}
                        >
                          {row.is_closed ? '—' : to12h(row.closing_time)}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableFrame>
            )}
          </>
        )}
      </QueryGate>
    </>
  );
  // Embedded in the Opening hours step, the row is the heading already, so
  // the table stands on its own; the page keeps its "Weekly hours" block.
  const block = embedded ? (
    <>
      {body}
      <div className="flex justify-end pt-2 empty:hidden">{savedNote}</div>
    </>
  ) : (
    <Block first title="Weekly hours" control={savedNote}>
      {body}
    </Block>
  );

  const footer = (
    <SaveBar
      dirty={dirty}
      saving={saving}
      error={error}
      onSave={save}
      onDiscard={() => {
        setDraft(base);
        setError(null);
        setInvalidDay(null);
        setCopied(null);
      }}
      summary={`${changed} ${changed === 1 ? 'day' : 'days'} changed`}
      saveLabel="Save hours"
    />
  );

  return { block, footer, dirty };
}

/**
 * The week, editable: one row per day with its hours side by side, a
 * "Copy to…" on each open day, and the open/closed switch. On a phone the
 * hours drop under the day, so nothing scrolls sideways or gets cut off.
 */
const WeeklyEditList: React.FC<{
  rows: WeeklyHoursDraft[];
  todayDay: string | undefined;
  invalidDay: string | null;
  saving: boolean;
  embedded: boolean;
  onChange: (day: string, patch: Partial<WeeklyHoursDraft>) => void;
  onCopy?: (sourceDay: string, targets: string[]) => void;
  /** Days the last copy changed, tinted while it can still be undone. */
  copiedDays?: string[];
}> = ({
  rows,
  todayDay,
  invalidDay,
  saving,
  embedded,
  onChange,
  onCopy,
  copiedDays,
}) => {
  const [copying, setCopying] = useState<string | null>(null);
  // Points at the "Copy to…" that was pressed; set before the popover opens.
  const anchor = useRef<HTMLElement | null>(null);
  // The first open day's copy is tonal, so the shortcut is found before
  // someone sets all seven days by hand.
  const featured = rows.find((r) => !r.is_closed)?.day_of_week;
  return (
    <>
      <ul
        aria-label="Weekly hours"
        className={clsx(
          'mt-3 border-t border-v2-line/80',
          !embedded && '-mx-4 lg:-mx-6',
        )}
      >
        {rows.map((row) => {
          const day = row.day_of_week;
          const invalid = invalidDay === day;
          return (
            <li
              key={day}
              className={clsx(
                'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-4 gap-y-2 border-b border-v2-line/80 py-2.5 transition-colors duration-300 sm:grid-cols-[148px_minmax(0,1fr)_auto_auto]',
                // Embedded rows keep a small inset so a tint has an edge.
                embedded ? 'px-2' : 'px-4 lg:px-6',
                invalid
                  ? 'bg-v2-danger/[0.04]'
                  : copiedDays?.includes(day) && 'bg-v2-accent/[0.05]',
              )}
            >
              <span className="inline-flex min-w-0 items-center gap-2 text-v2-13 font-medium text-v2-fg">
                {day}
                {day === todayDay && <Tag>Today</Tag>}
              </span>
              <span className="col-span-3 row-start-2 flex min-w-0 items-center gap-2 sm:col-span-1 sm:col-start-2 sm:row-start-1">
                {row.is_closed ? (
                  <span className="text-v2-13 text-v2-fg-4">
                    Closed all day
                  </span>
                ) : (
                  <>
                    <TimeInput
                      size="sm"
                      className="w-[116px]"
                      suggest={SUGGEST.opens}
                      aria-label={`${day} opens`}
                      value={row.opening_time}
                      invalid={invalid && !row.opening_time}
                      disabled={saving}
                      onChange={(value) =>
                        onChange(day, { opening_time: value })
                      }
                    />
                    <span aria-hidden className="text-v2-fg-4">
                      –
                    </span>
                    <TimeInput
                      size="sm"
                      className="w-[116px]"
                      suggest={SUGGEST.closes}
                      aria-label={`${day} closes`}
                      value={row.closing_time}
                      invalid={invalid && !row.closing_time}
                      disabled={saving}
                      onChange={(value) =>
                        onChange(day, { closing_time: value })
                      }
                    />
                  </>
                )}
              </span>
              {onCopy && !row.is_closed && (
                <Button
                  size="sm"
                  variant={day === featured ? 'tonal' : 'ghost'}
                  leading={<IconCopy size={13} />}
                  aria-label={`Copy ${day}’s hours to other days`}
                  aria-haspopup="dialog"
                  aria-expanded={copying === day}
                  disabled={saving}
                  className="col-start-2 row-start-1 justify-self-end sm:col-start-3"
                  onClick={(e) => {
                    anchor.current = e.currentTarget;
                    setCopying(day);
                  }}
                >
                  {/* On a phone the icon alone, beside the switch. */}
                  <span className="hidden sm:inline">Copy to…</span>
                </Button>
              )}
              {/* The row says the state in words (times, or "Closed all day"). */}
              <Switch
                hideWord
                label={`${day} open`}
                checked={!row.is_closed}
                disabledReason={saving ? 'Saving your hours' : undefined}
                className="col-start-3 row-start-1 justify-self-end sm:col-start-4"
                onChange={(next) => onChange(day, { is_closed: !next })}
              />
            </li>
          );
        })}
      </ul>
      {copying && (
        // Mounted per opening, so its picks start fresh for that day.
        <CopyHoursPopover
          key={copying}
          sourceDay={copying}
          rows={rows}
          anchorRef={anchor}
          onClose={() => setCopying(null)}
          onApply={(targets) => {
            onCopy?.(copying, targets);
            setCopying(null);
          }}
        />
      )}
    </>
  );
};

const WEEKEND = ['Saturday', 'Sunday'];

/** "Tuesday", "Tuesday and Wednesday", up to three names, then "4 days". */
const dayList = (days: string[]) =>
  days.length > 3
    ? `${days.length} days`
    : days.length <= 1
      ? (days[0] ?? '')
      : `${days.slice(0, -1).join(', ')} and ${days[days.length - 1]}`;

/**
 * "Copy to…": put the source day's hours on other days in one go. Presets
 * pick the usual groups; opening preselects the rest of the source's half of
 * the week. It only edits the draft — Save still decides — and days whose
 * hours would change are named before they are replaced.
 */
const CopyHoursPopover: React.FC<{
  sourceDay: string;
  rows: WeeklyHoursDraft[];
  anchorRef: React.RefObject<HTMLElement>;
  onClose: () => void;
  onApply: (targets: string[]) => void;
}> = ({ sourceDay, rows, anchorRef, onClose, onApply }) => {
  const source = rows.find((r) => r.day_of_week === sourceDay);
  const days = rows.map((r) => r.day_of_week);
  const others = (set: string[]) => set.filter((d) => d !== sourceDay);
  const weekdays = others(days.filter((d) => !WEEKEND.includes(d)));
  const weekend = others(days.filter((d) => WEEKEND.includes(d)));
  const [selected, setSelected] = useState(() =>
    WEEKEND.includes(sourceDay) ? weekend : weekdays,
  );

  if (!source) return null;
  const sameSet = (a: string[], b: string[]) =>
    a.length > 0 && a.length === b.length && a.every((d) => b.includes(d));
  const toggle = (day: string, on: boolean) =>
    setSelected((cur) =>
      on
        ? days.filter((d) => d === day || cur.includes(d))
        : cur.filter((d) => d !== day),
    );
  const replacing = rows.filter(
    (r) =>
      selected.includes(r.day_of_week) &&
      !r.is_closed &&
      (r.opening_time !== source.opening_time ||
        r.closing_time !== source.closing_time),
  );

  return (
    <Popover
      open
      onClose={onClose}
      anchorRef={anchorRef}
      title={`Copy ${sourceDay}’s hours`}
      footer={
        <>
          {selected.length === 0 && (
            <Reason className="mr-auto">Pick at least one day.</Reason>
          )}
          <Button
            variant="primary"
            disabledReason={
              selected.length === 0 ? 'Pick at least one day' : undefined
            }
            onClick={() => onApply(selected)}
          >
            {selected.length === 0
              ? 'Apply'
              : `Apply to ${selected.length} ${selected.length === 1 ? 'day' : 'days'}`}
          </Button>
        </>
      }
    >
      <p className="mb-4 flex items-center gap-2 rounded-v2 bg-v2-accent/[0.07] px-2.5 py-2 text-v2-13">
        <IconClock size={14} className="shrink-0 text-v2-accent" />
        <span className="v2-num font-medium text-v2-fg">
          {source.is_closed
            ? 'Closed all day'
            : `${to12h(source.opening_time)} – ${to12h(source.closing_time)}`}
        </span>
        <span className="text-v2-12 text-v2-fg-3">{sourceDay}</span>
      </p>
      <p className="mb-2 text-v2-11 font-medium uppercase tracking-wide text-v2-fg-3">
        Apply to
      </p>
      <div
        role="group"
        aria-label="Quick picks"
        className="mb-3 flex flex-wrap items-center gap-1.5"
      >
        <Chip
          pressed={sameSet(selected, weekdays)}
          onClick={() => setSelected(weekdays)}
        >
          Weekdays
        </Chip>
        <Chip
          pressed={sameSet(selected, weekend)}
          onClick={() => setSelected(weekend)}
        >
          Weekend
        </Chip>
        <Chip
          pressed={sameSet(selected, others(days))}
          onClick={() => setSelected(others(days))}
        >
          Every day
        </Chip>
        {selected.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => setSelected([])}>
            Clear
          </Button>
        )}
      </div>
      <div
        role="group"
        aria-label={`Days to copy ${sourceDay}’s hours to`}
        className="grid grid-cols-2 gap-1.5"
      >
        {rows.map((row) => {
          const day = row.day_of_week;
          const isSource = day === sourceDay;
          const checked = selected.includes(day);
          return (
            <label
              key={day}
              className={clsx(
                'flex min-h-[38px] items-center gap-2 rounded-v2 border px-2.5 py-1.5 text-v2-13',
                isSource
                  ? 'cursor-not-allowed border-v2-line bg-v2-sunken text-v2-fg-4'
                  : checked
                    ? 'cursor-pointer border-v2-accent/50 bg-v2-accent/[0.05] text-v2-fg'
                    : 'cursor-pointer border-v2-line text-v2-fg-2 hover:border-v2-line-strong',
              )}
            >
              <input
                type="checkbox"
                disabled={isSource}
                checked={!isSource && checked}
                onChange={(e) => toggle(day, e.target.checked)}
                className="h-4 w-4 rounded-v2-sm border-v2-line-strong bg-v2-surface text-v2-accent focus:ring-2 focus:ring-v2-accent/25 focus:ring-offset-0 disabled:opacity-50"
              />
              <span className="min-w-0 flex-1 truncate">{day}</span>
              {isSource && (
                <span
                  aria-hidden
                  className="text-v2-10 font-medium uppercase tracking-wide"
                >
                  Source
                </span>
              )}
            </label>
          );
        })}
      </div>
      {replacing.length > 0 && (
        <Callout tone="warning" well className="mt-3">
          Hours on {dayList(replacing.map((r) => r.day_of_week))} will be
          replaced.
        </Callout>
      )}
    </Popover>
  );
};

const TableFrame: React.FC<{
  embedded: boolean;
  children: React.ReactNode;
}> = ({ embedded, children }) =>
  embedded ? (
    <div className="v2-scroll mt-4 overflow-x-auto">{children}</div>
  ) : (
    <>{children}</>
  );

/**
 * Where the weekly hours come from: the POS (read-only here) or this
 * dashboard. Saves as soon as it changes.
 */
const SourceChoice: React.FC<{
  source: string;
  manual: boolean;
  switching: boolean;
  switchError: string | null;
  onChange: (manual: boolean) => void;
}> = ({ source, manual, switching, switchError, onChange }) => (
  <>
    <div
      role="radiogroup"
      aria-label="Schedule source"
      className="flex flex-wrap items-start gap-x-8 gap-y-2"
    >
      <Radio
        name="hours-source"
        label={`Automatic from ${hoursSourceLabel(source, false)}`}
        description="Follows your point of sale. Read-only here."
        checked={!manual}
        disabled={switching}
        onChange={() => onChange(false)}
      />
      <Radio
        name="hours-source"
        label="Manual"
        description="Set the hours on this dashboard."
        checked={manual}
        disabled={switching}
        onChange={() => onChange(true)}
      />
      {switching && (
        <span
          className="inline-flex items-center gap-1.5 text-v2-12 text-v2-fg-3"
          role="status"
        >
          <Spinner /> Switching…
        </span>
      )}
    </div>
    {switchError && (
      <Callout tone="danger" className="mt-3 max-w-[640px]">
        {switchError}
      </Callout>
    )}
    {!manual && (
      <Callout tone="neutral" className="mt-3 max-w-[640px]">
        Hours are pulled from your {hoursSourceLabel(source, false)} listing.
        Switch to manual to edit them here.
      </Callout>
    )}
  </>
);

/** Where an empty time's list opens: a typical lunch-to-late day. */
const SUGGEST = { opens: '11:00', closes: '22:00' } as const;

// ---------------------------------------------------------------------------
// Special hours
// ---------------------------------------------------------------------------

type SpecialDraft = {
  id?: number;
  date: string;
  is_closed: boolean;
  holiday_name: string;
  special_info: string;
  opening_time: string;
  closing_time: string;
};

const SpecialBlock: React.FC = () => {
  const specialQuery = useSpecialHoursQuery();
  const infoQuery = useStoreInfoQuery();
  const hoursActions = useHoursActions();
  const special = combineQueries(
    [specialQuery, infoQuery],
    () => ({
      rows: [...(specialQuery.data ?? [])].sort((a, b) =>
        a.date.localeCompare(b.date),
      ),
      today: infoQuery.data!.today_local_date,
    }),
    'Special hours could not be loaded.',
  );
  const [editing, setEditing] = useState<SpecialDraft | null>(null);
  const [deleting, setDeleting] = useState<SpecialHour | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, markSaved] = useSavedAt();

  const openAdd = (today: string) => {
    setEditing({
      date: today,
      is_closed: false,
      holiday_name: '',
      special_info: '',
      opening_time: '',
      closing_time: '',
    });
    setError(null);
  };
  const openEdit = (sh: SpecialHour) => {
    setEditing({
      id: sh.id,
      date: sh.date,
      is_closed: sh.is_closed,
      holiday_name: sh.holiday_name ?? '',
      special_info: sh.special_info ?? '',
      opening_time: normalizeTime(sh.opening_time),
      closing_time: normalizeTime(sh.closing_time),
    });
    setError(null);
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.date) return setError('Choose a date.');
    if (!editing.is_closed && (!editing.opening_time || !editing.closing_time))
      return setError(
        'Set both opening and closing times, or mark the day closed.',
      );
    setBusy(true);
    setError(null);
    const body = {
      date: editing.date,
      is_closed: editing.is_closed,
      holiday_name: editing.holiday_name.trim() || null,
      special_info: editing.special_info.trim(),
      opening_time: editing.is_closed ? null : `${editing.opening_time}:00`,
      closing_time: editing.is_closed ? null : `${editing.closing_time}:00`,
    };
    try {
      if (editing.id) await hoursActions.updateSpecial(editing.id, body);
      else await hoursActions.createSpecial(body);
      setEditing(null);
      markSaved();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Could not save the special date.',
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting?.id) return;
    setBusy(true);
    setError(null);
    try {
      await hoursActions.deleteSpecial(deleting.id);
      setDeleting(null);
      setEditing(null);
      markSaved();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Could not delete the special date.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Block
      title="Special hours"
      description="Holidays, early closures or one-off hours. On these dates Arthur quotes this instead of the weekly schedule."
      control={
        <>
          <SavedNote when={savedAt} />
          <Button
            size="sm"
            variant="secondary"
            leading={<IconPlus size={14} />}
            onClick={() => openAdd(special.data?.today ?? '')}
            disabled={special.status !== 'success'}
          >
            Add date
          </Button>
        </>
      }
    >
      <QueryGate
        query={special}
        skeleton={
          <Table fixed>
            <tbody>
              <TableSkeletonRows rows={3} cols={6} />
            </tbody>
          </Table>
        }
      >
        {({ rows, today }) =>
          rows.length === 0 ? (
            <EmptyState
              compact
              icon={<IconCalendar />}
              title="No special dates"
              description="Arthur uses the weekly schedule until you add a holiday or a one-off change."
              action={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => openAdd(today)}
                >
                  Add date
                </Button>
              }
            />
          ) : (
            <Table
              fixed
              className="-mx-4 w-[calc(100%+32px)] lg:-mx-6 lg:w-[calc(100%+48px)]"
            >
              <THead>
                <tr>
                  <TH width={176}>Date</TH>
                  <TH>Name</TH>
                  <TH>Special info</TH>
                  <TH width={140}>Status</TH>
                  <TH width={180}>Hours</TH>
                  <TH width={56} align="right">
                    <span className="sr-only">Actions</span>
                  </TH>
                </tr>
              </THead>
              <TBody>
                {rows.map((sh) => (
                  <TR key={sh.id ?? sh.date} onActivate={() => openEdit(sh)}>
                    <TD num className="font-medium">
                      {formatDate(sh.date, { weekday: true })}
                      {sh.date < today && <Tag className="ml-2">Past</Tag>}
                    </TD>
                    <TD
                      truncate
                      className={clsx(!sh.holiday_name && 'text-v2-fg-4')}
                    >
                      {sh.holiday_name || '—'}
                    </TD>
                    <TD
                      truncate
                      className={clsx(!sh.special_info && 'text-v2-fg-4')}
                      title={sh.special_info || undefined}
                    >
                      {sh.special_info || '—'}
                    </TD>
                    <TD>
                      <StatusLabel
                        size="sm"
                        tone={sh.is_closed ? 'warning' : 'accent'}
                      >
                        {sh.is_closed ? 'Closed' : 'Modified hours'}
                      </StatusLabel>
                    </TD>
                    <TD num className="text-v2-fg-2">
                      {sh.is_closed
                        ? 'Closed all day'
                        : `${to12h(normalizeTime(sh.opening_time))} – ${to12h(normalizeTime(sh.closing_time))}`}
                    </TD>
                    <TD align="right">
                      <RowMenu
                        label={`Actions for ${sh.holiday_name || formatDate(sh.date)}`}
                        onEdit={() => openEdit(sh)}
                        onDelete={() => setDeleting(sh)}
                      />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )
        }
      </QueryGate>

      <Dialog
        open={editing !== null}
        onClose={() => !busy && setEditing(null)}
        title={editing?.id ? 'Edit special hours' : 'Add special hours'}
        description="Arthur quotes these hours for this date instead of the weekly schedule."
        size="md"
        footer={
          <>
            {editing?.id && (
              <Button
                variant="danger"
                className="mr-auto"
                disabled={busy}
                onClick={() => {
                  const row = special.data?.rows.find(
                    (r) => r.id === editing.id,
                  );
                  if (row) setDeleting(row);
                }}
              >
                Delete…
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => setEditing(null)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={save} loading={busy}>
              {editing?.id ? 'Save' : 'Add date'}
            </Button>
          </>
        }
      >
        {editing && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label="Date">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  className="v2-num"
                  min={special.data?.today}
                  value={editing.date}
                  onChange={(e) =>
                    setEditing({ ...editing, date: e.target.value })
                  }
                  data-autofocus="true"
                />
              )}
            </Field>
            <Field label="Name" optional>
              {(id) => (
                <Input
                  id={id}
                  placeholder="e.g. Christmas Day"
                  value={editing.holiday_name}
                  onChange={(e) =>
                    setEditing({ ...editing, holiday_name: e.target.value })
                  }
                />
              )}
            </Field>
            <Field label="Special info" optional className="col-span-2">
              {(id) => (
                <Textarea
                  id={id}
                  rows={3}
                  maxLength={500}
                  placeholder="e.g. Brunch served from 10:00 AM to 2:00 PM"
                  value={editing.special_info}
                  onChange={(e) =>
                    setEditing({ ...editing, special_info: e.target.value })
                  }
                />
              )}
            </Field>
            <div className="col-span-2">
              <Checkbox
                label="Closed all day"
                checked={editing.is_closed}
                onChange={(e) =>
                  setEditing({ ...editing, is_closed: e.target.checked })
                }
              />
            </div>
            {!editing.is_closed && (
              <>
                <Field label="Opens">
                  {(id) => (
                    <TimeInput
                      id={id}
                      suggest={SUGGEST.opens}
                      value={editing.opening_time}
                      invalid={!!error && !editing.opening_time}
                      onChange={(value) =>
                        setEditing({ ...editing, opening_time: value })
                      }
                    />
                  )}
                </Field>
                <Field label="Closes">
                  {(id) => (
                    <TimeInput
                      id={id}
                      suggest={SUGGEST.closes}
                      value={editing.closing_time}
                      invalid={!!error && !editing.closing_time}
                      onChange={(value) =>
                        setEditing({ ...editing, closing_time: value })
                      }
                    />
                  )}
                </Field>
              </>
            )}
            {error && (
              <Callout tone="danger" className="col-span-2">
                {error}
              </Callout>
            )}
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
        title="Delete special hours?"
        confirmLabel="Delete"
        busy={busy}
        consequence={
          deleting
            ? `Removes ${deleting.holiday_name ? `“${deleting.holiday_name}”` : `the entry for ${formatDate(deleting.date)}`}. Arthur goes back to the weekly schedule for that day. This can't be undone.`
            : ''
        }
      >
        {error && <Callout tone="danger">{error}</Callout>}
      </ConfirmDialog>
    </Block>
  );
};

export default HoursSection;
